import { useElectronAPI } from "@circle-react/hooks/utils/useElectronAPI";
import { useCallback, useEffect, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

import { VIDEO_CONSTRAINTS } from "../constants";
import { cleanupScreenShare } from "./mediaStreamUtils";
import type { StreamStatus } from "./mediaTypes";

interface UseScreenStreamReturn {
  cleanup: () => void;
  error: Error | null;
  isScreenSharing: boolean;
  screenStream: MediaStream | null;
  startScreenShare: () => Promise<void>;
  status: StreamStatus;
  stopScreenShare: () => void;
}

export const useScreenStream = (): UseScreenStreamReturn => {
  const screenStreamRef = useRef<MediaStream | null>(null);
  const screenEndedHandlerRef = useRef<(() => void) | null>(null);
  const isScreenRequestingRef = useRef(false);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [status, setStatus] = useState<StreamStatus>("idle");
  const [error, setError] = useState<Error | null>(null);
  const { error: showError } = useToast();
  const { isElectron } = useElectronAPI();

  const startScreenShare = useCallback(async () => {
    if (isElectron) {
      showError(t("live_streams.room.electron_screen_sharing_browser_error"));
      return;
    }
    if (isScreenRequestingRef.current) return;
    isScreenRequestingRef.current = true;

    try {
      cleanupScreenShare(screenStreamRef, screenEndedHandlerRef);
      setError(null);
      setStatus("requesting");
      const stream = await navigator.mediaDevices.getDisplayMedia({
        audio: true,
        video: VIDEO_CONSTRAINTS,
      });
      screenStreamRef.current = stream;
      setScreenStream(stream);
      setStatus("active");
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        const handleEnded = () => {
          screenStreamRef.current = null;
          screenEndedHandlerRef.current = null;
          setScreenStream(null);
          setStatus("idle");
        };
        screenEndedHandlerRef.current = handleEnded;
        videoTrack.addEventListener("ended", handleEnded);
      }
    } catch (err) {
      setError(err as Error);
      setStatus("error");
    } finally {
      isScreenRequestingRef.current = false;
    }
  }, [isElectron, showError]);

  const stopScreenShare = useCallback(() => {
    cleanupScreenShare(screenStreamRef, screenEndedHandlerRef);
    setScreenStream(null);
    setStatus("idle");
  }, []);

  const cleanup = useCallback(() => {
    stopScreenShare();
    setError(null);
  }, [stopScreenShare]);

  useEffect(() => cleanup, [cleanup]);

  return {
    cleanup,
    error,
    isScreenSharing: screenStream !== null,
    screenStream,
    startScreenShare,
    status,
    stopScreenShare,
  };
};
